/**
 * Permission model
 * @class
 */
import Base from '@netx/core/lib/netx-model';

export default class Permission extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		return 'permissionId';
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object}  defaults                          - The default permission values
	 * @property {number}  defaults.permissionsId            - The default permission id
	 * @property {string}  defaults.type                     - The default permission type (read/deny)
	 * @property {string}  defaults.mode                     - The default permission mode
	 * @property {object}  defaults.resource                 - The default permission resource
	 * @property {number}  defaults.resource.id              - The default permission resource id
	 * @property {string}  defaults.resource.type            - The default permission resource type
	 * @property {string}  defaults.resource.scope           - The default permission resource scope
	 * @property {number}  defaults.resource.attributeScope  - The default permission resource attribute scope
	 * @property {object}  defaults.userSelector             - The default permission user selector https://kb.netx.net/display/DEV/UserSelector
	 * @property {boolean} defaults.userSelector.all         - The default permission user selector all
	 * @property {array}   defaults.userSelector.emails      - The default permission user selector emails
	 * @property {array}   defaults.userSelector.groupIds    - The default permission user selector group ids
	 * @property {boolean} defaults.userSelector.none        - The default permission user selector none
	 * @property {array}   defaults.userSelector.userIds     - The default permission user selector user ids
	 * @property {array}   defaults.category                 - The default permission category scope
	 * @property {array}   defaults.resourceScopes           - The default permission resource scopes
	 */
	get defaults() {
		return {
			// permissionId : 0,
			type: 'permit',
			mode: 'read',
			resource: {
				id: 0,
				type: '',
				scope: '',
				attributeScope: 0,
			},
			userSelector: {
				all: false,
				emails: [],
				groupIds: [],
				none: false,
				userIds: [],
			},
			categoryScope: [],
			resourceScopes: [],
		};
	}

	/**
	 * @property {object} methods         - Model RPC methods
	 * @property {array}  methods.create  - Create new permission
	 * @property {array}  methods.remove  - Destroy permission
	 * @property {array}  methods.update  - Update permission
	 */
	get methods() {
		return {
			create(opts) {
				return ['setPermission', this.self()];
			},
			remove: ['deletePermission', 'permissionId'],
			update(opts) {
				return ['setPermission', this.self()];
			},
		};
	}

	/** @override */
	constructor(attrs, opts) {
		super(attrs, opts);

		// Hey, listen!
		this.listenTo(this, 'change:mode', (model, mode) => {
			// `attribute` resources do not have a category scope when in `read` mode
			if (mode === 'read' && this.getResourceType() === 'attribute') {
				this.set('categoryScope', []);
			}
		});
	}

	/**
	 * @method
	 * @param {string} type Selector type (user/group)
	 * @param {array} ids Selector ids
	 * @param {object} opts Options
	 */
	addSelectorItems(type, ids, opts = {}) {
		if (!_.isArray(ids)) ids = [ids];

		const userSelector = this.get('userSelector') || {};
		const existingIds = userSelector[`${type}Ids`];

		const newIds = _.compact(
			ids.map((id) => (existingIds.indexOf(id) > -1 ? null : id)),
		);

		if (!newIds.length) return this;

		userSelector[`${type}Ids`] = existingIds.concat(newIds);

		if (opts.silent) return this;

		return this.trigger('change', this, opts)
			.trigger('change:userSelector', this, userSelector, opts)
			.trigger(`userSelector:add:${type}Ids`, this, newIds, opts);
	}

	/**
	 * @method
	 * @return {string}
	 */
	getResourceType() {
		return this.get('resource').type || '';
	}

	/**
	 * @method
	 * @return {boolean} Has selectors
	 */
	hasSelectors() {
		const userSelector = this.get('userSelector');
		return !(
			!userSelector.all &&
			!userSelector.groupIds.length &&
			!userSelector.userIds.length
		);
	}

	/**
	 * @method
	 * @param {object} data Data
	 * @param {object} opts Options
	 */
	parseAndSave(data, opts) {
		return this.save(
			_.pick.apply(_, [data].concat(_.keys(this.getDefaults()))),
			opts,
		);
	}

	/**
	 * @method
	 * @param {string} type Selector type (user/group)
	 * @param {array} ids Selector ids
	 * @param {object} opts Options
	 */
	removeSelectorItems(type, ids, opts = {}) {
		if (!_.isArray(ids)) ids = [ids];

		const userSelector = this.get('userSelector') || {};
		const existingIds = userSelector[`${type}Ids`];

		const removedIds = _.compact(
			ids.map((id) => {
				const idx = existingIds.indexOf(id);
				if (idx < 0) return;

				existingIds.splice(idx, 1);
				return id;
			}),
		);

		if (!removedIds.length) return;

		userSelector[`${type}Ids`] = existingIds;

		if (opts.silent) return this;

		return this.trigger('change', this, opts)
			.trigger('change:userSelector', this, userSelector, opts)
			.trigger(`userSelector:remove:${type}Ids`, this, removedIds, opts);
	}

	/**
	 * @method
	 * @param {boolean} selectAll Select all
	 * @param {object} opts Options
	 */
	toggleSelectAll(selectAll, opts = {}) {
		const userSelector = this.get('userSelector');

		if (selectAll == null) selectAll = !userSelector.all;

		userSelector.all = selectAll;

		if (opts.silent) return this;

		return this.trigger('change', this, opts)
			.trigger('change:userSelector', this, userSelector, opts)
			.trigger('change:userSelector:all', this, selectAll, opts);
	}

	/** @type {string} */
	static get __name__() {
		return 'Permission';
	}
}
